/* PMW theme emitters. Custom Pixel/GTM own consent and downstream delivery. */
(() => {
  'use strict';
  if (window.pmwAnalyticsEmittersReady) return;
  window.pmwAnalyticsEmittersReady = true;
  const MAX_QUEUE = 20, MAX_AGE = 5000, RETRY = 250;
  const queue = [], sent = new Set(), pending = new Set();
  const observed = new Map(), keyboard = new WeakMap();
  let retryTimer = 0, scanTimer = 0, batchTimer = 0;
  const text = value => typeof value === 'string' ? value.trim() : '';
  const numericId = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  const parse = value => { try { return JSON.parse(value || 'null'); } catch (_) { return null; } };

  function remove(job, success) {
    const index = queue.indexOf(job);
    if (index === -1) return;
    queue.splice(index, 1);
    job.keys.forEach(key => { pending.delete(key); if (success) sent.add(key); });
  }
  function flush() {
    clearTimeout(retryTimer);
    retryTimer = 0;
    const analytics = window.Shopify?.analytics;
    queue.slice().forEach(job => {
      if (Date.now() - job.created >= MAX_AGE) { remove(job, false); return; }
      if (job.inFlight || typeof analytics?.publish !== 'function') return;
      try {
        const result = analytics.publish(job.name, job.data);
        if (result && typeof result.then === 'function') {
          job.inFlight = true;
          Promise.resolve(result).then(ok => {
            if (ok !== false) remove(job, true);
            else job.inFlight = false;
          }, () => { job.inFlight = false; });
        } else if (result !== false) remove(job, true);
      } catch (_) { /* Retry briefly; never affect the native action. */ }
    });
    if (queue.length) retryTimer = setTimeout(flush, RETRY);
  }
  function publish(name, data, keys = []) {
    if (queue.length >= MAX_QUEUE) remove(queue[0], false);
    keys.forEach(key => pending.add(key));
    queue.push({ name, data, keys, created: Date.now(), inFlight: false });
    flush();
  }
  function destination(value) {
    try {
      const url = new URL(value, location.href);
      return /^https?:$/.test(url.protocol) ? url.origin + url.pathname : '';
    } catch (_) { return ''; }
  }
  function sendClick(control) {
    const kind = control.dataset.pmwEvent;
    if (!['booking_click', 'availability_click', 'newsletter_click'].includes(kind) || control.disabled) return;
    const data = {
      cta_id: text(control.dataset.pmwCtaId),
      cta_location: text(control.dataset.pmwCtaLocation),
      cta_text: text(control.getAttribute('aria-label') || control.textContent)
    };
    if (!data.cta_id || !data.cta_location) return;
    if (kind === 'newsletter_click') {
      data.form_id = text(control.dataset.pmwFormId);
      if (!data.form_id) return;
    } else {
      const href = control.dataset.pmwDestination || control.getAttribute('href');
      if (!href || /^(?:mailto:|tel:)/i.test(href)) return;
      // Contact-only anchors are never reservation events.
      if (kind !== 'newsletter_click' && /^#/.test(href) && data.cta_id !== 'header_booking') return;
      data.destination_url = destination(href);
      if (!data.destination_url) return;
      const host = new URL(data.destination_url).hostname;
      if (host === 'palacmalawies.premiumhotel.pl') data.booking_engine = 'betasi';
      else if (/(^|\.)profitroom\.(com|pl)$/.test(host)) data.booking_engine = 'profitroom';
      const product = parse(control.dataset.pmwProduct);
      if (product && numericId(product.product_id)) {
        data.product_id = product.product_id;
        if (['room', 'package', 'offer', 'voucher'].includes(product.product_type)) data.product_type = product.product_type;
      }
    }
    publish('pmw:' + kind, data);
  }
  document.addEventListener('click', event => {
    const control = event.target.closest?.('[data-pmw-event]');
    if (!control || event.button !== 0) return;
    const activation = control.form && keyboard.get(control.form);
    if (activation) activation.handled = true;
    sendClick(control);
  }, true);
  // Native implicit submission usually dispatches click. Fallback covers Enter
  // without click, including a form blocked by constraint validation.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || event.repeat || event.isComposing) return;
    const input = event.target;
    if (input.tagName !== 'INPUT' || !['email', 'text', 'search', 'url', 'tel'].includes(input.type)) return;
    const form = input.form;
    const control = form?.querySelector('[data-pmw-event="newsletter_click"]');
    if (!control || control.disabled) return;
    const activation = { handled: false };
    keyboard.set(form, activation);
    setTimeout(() => {
      if (!activation.handled && !event.defaultPrevented) sendClick(control);
      if (keyboard.get(form) === activation) keyboard.delete(form);
    }, 0);
  }, true);

  function layoutVisible(element) {
    if (!element.isConnected || !element.getClientRects().length) return false;
    for (let node = element; node && node.nodeType === 1; node = node.parentElement) {
      if (node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    return true;
  }
  function listContext(root) {
    let id = text(root.dataset.pmwListId), name = text(root.dataset.pmwListName) || id;
    const type = root.dataset.pmwListFilter;
    if (type === 'room' || type === 'package') {
      const attr = type === 'room' ? 'data-room-filter' : 'data-package-filter';
      const button = root.querySelector('[' + attr + '].is-active');
      const filter = text(button?.getAttribute(attr)) || 'all';
      id += '_' + filter;
      name += ' - ' + text(button?.querySelector('strong')?.textContent || filter);
    }
    return { id, name };
  }
  function itemData(card, index) {
    const raw = parse(card.dataset.pmwItem);
    if (!raw || !numericId(raw.item_id) || !text(raw.item_name)) return null;
    const item = { item_id: raw.item_id, item_name: text(raw.item_name), index };
    if (numericId(raw.item_variant)) item.item_variant = raw.item_variant;
    if (text(raw.item_category)) item.item_category = text(raw.item_category);
    if (text(raw.item_brand)) item.item_brand = text(raw.item_brand);
    if (typeof raw.price === 'number' && Number.isFinite(raw.price) && raw.price >= 0) item.price = raw.price;
    return item;
  }
  function collect() {
    batchTimer = 0;
    const groups = new Map();
    document.querySelectorAll('[data-pmw-list-id]').forEach(root => {
      const context = listContext(root);
      if (!context.id) return;
      const offset = Math.max(0, Number(root.dataset.pmwListOffset || root.querySelector('[data-pmw-list-offset]')?.dataset.pmwListOffset) || 0);
      const cards = [...root.querySelectorAll('[data-pmw-item]')].filter(card => card.closest('[data-pmw-list-id]') === root && layoutVisible(card));
      cards.forEach((card, index) => {
        if (!observed.get(card)) return;
        const item = itemData(card, offset + index);
        if (!item) return;
        const key = JSON.stringify([context.id, item.item_id, item.item_variant || '']);
        if (sent.has(key) || pending.has(key)) return;
        const groupKey = JSON.stringify([context.id, context.name]);
        if (!groups.has(groupKey)) groups.set(groupKey, { context, items: [], keys: new Set() });
        const group = groups.get(groupKey);
        if (group.keys.has(key)) return;
        group.keys.add(key);
        group.items.push({ item, key });
      });
    });
    groups.forEach(group => {
      for (let start = 0; start < group.items.length; start += 200) {
        const batch = group.items.slice(start, start + 200);
        publish('pmw:view_item_list', { item_list_id: group.context.id, item_list_name: group.context.name, items: batch.map(x => x.item) }, batch.map(x => x.key));
      }
    });
  }
  function scheduleCollect() {
    if (!batchTimer) batchTimer = setTimeout(collect, 80);
  }
  if (typeof IntersectionObserver !== 'function') return;
  const intersection = new IntersectionObserver(entries => {
    entries.forEach(entry => observed.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.5));
    scheduleCollect();
  }, { threshold: [0, 0.5, 1] });
  function scan() {
    scanTimer = 0;
    observed.forEach((_, card) => { if (!card.isConnected) { intersection.unobserve(card); observed.delete(card); } });
    document.querySelectorAll('[data-pmw-item]').forEach(card => {
      if (observed.has(card) || !card.closest('[data-pmw-list-id]')) return;
      observed.set(card, false);
      intersection.observe(card);
    });
    scheduleCollect();
  }
  function scheduleScan() { if (!scanTimer) scanTimer = setTimeout(scan, 40); }
  function init() {
    scan();
    new MutationObserver(scheduleScan).observe(document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['hidden', 'class', 'style', 'aria-hidden', 'aria-selected', 'aria-pressed', 'data-pmw-item', 'data-pmw-list-id', 'data-pmw-list-offset']
    });
  }
  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('shopify:section:unload', scheduleScan);
  window.addEventListener('pageshow', scheduleScan);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleScan(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();